<template lang="pug">
    section
        .results-filter-toolbar.d-none.d-md-flex
            .results-filter-toolbar__search
                b-form-input(v-model="bib_name_q" :placeholder="$t('races.show.name_or_bib_placeholder')")
                b-form-checkbox.mt-1(switch v-model="bib_name_exact") {{$t('races.show.name_or_bib_exact')}}
            .results-filter-toolbar__gender(v-if="genderFilterEnabled")
                .results-gender-filter
                    small.results-gender-filter__label {{$t('races.show.gender_filters.label')}}
                    b-form-radio-group.results-gender-filter__switch(
                        size="sm" buttons button-variant="outline-secondary"
                        v-model="q.gender_eq"
                        :aria-label="$t('races.show.gender_filters.label')"
                    )
                        b-form-radio(
                            v-for="option in gender_options"
                            :key="String(option.value)"
                            :value="option.value"
                            :title="option.title"
                            :aria-label="option.title"
                        )
                            template(v-if="option.value == null") {{option.text}}
                            template(v-else)
                                gender-icon(:gender="option.value" aria-hidden="true")
                                span.sr-only {{option.title}}
            .results-filter-toolbar__select(v-if="categoryFilterEnabled")
                b-form-select(v-model="q.category_eq" :options="race_categories")
                    template(#first)
                        option(value="" selected) -- {{$t('category')}} --
            .results-filter-toolbar__select(
                v-for="definition in additionalFilterDefinitions"
                :key="definition.key"
            )
                b-form-select(
                    v-model="public_filters[definition.key]"
                    :options="additionalFilterOptions(definition)"
                    :aria-label="definition.label"
                )
            .results-filter-toolbar__choices(v-if="extraFilters")
                b-form-radio-group(size="sm" buttons button-variant="outline-secondary")
                  b-form-radio(
                    v-for="extraFilter in extraFilters" :key="extraFilter.value"
                    v-model="q[extraFilter.term]" :value="extraFilter.value"
                  ) {{extraFilter.text || extraFilter.value }}
            .results-filter-toolbar__reset(v-show="hasActiveFilters")
                b-btn.results-filter-reset(size="sm" variant="link" @click="reset_filter()")
                    i.fal.fa-times.mr-1
                    |{{$t('search.form.reset')}}

        .results-filter-mobile.d-md-none.mb-3
            b-form-input.mb-1(v-model="bib_name_q" :placeholder="$t('races.show.name_or_bib_placeholder')")
            b-form-checkbox.mb-2(switch v-model="bib_name_exact") {{$t('races.show.name_or_bib_exact')}}

            .results-filter-mobile__actions(v-if="hasFilterPanelControls || hasActiveFilters")
                b-button.results-filter-toggle(
                    v-if="hasFilterPanelControls"
                    size="sm"
                    variant="light"
                    :class="{ 'is-open': additionalFiltersOpen }"
                    :aria-controls="additionalFiltersId"
                    :aria-expanded="additionalFiltersOpen ? 'true' : 'false'"
                    @click="additionalFiltersOpen = !additionalFiltersOpen"
                )
                    i.fal.fa-filter.mr-1
                    | {{$t('races.show.filters')}}
                    b-badge.results-filter-toggle__count.ml-1(v-if="filterPanelCount" variant="secondary") {{filterPanelCount}}
                b-btn.results-filter-reset(
                    v-show="hasActiveFilters"
                    size="sm"
                    variant="link"
                    @click="reset_filter()"
                )
                    i.fal.fa-times.mr-1
                    |{{$t('search.form.reset')}}

            b-collapse.mt-2(
                v-if="hasFilterPanelControls"
                :id="additionalFiltersId"
                v-model="additionalFiltersOpen"
            )
                .results-filter-mobile__control(v-if="genderFilterEnabled")
                    .results-gender-filter
                        small.results-gender-filter__label {{$t('races.show.gender_filters.label')}}
                        b-form-radio-group.results-gender-filter__switch(
                            size="sm" buttons button-variant="outline-secondary"
                            v-model="q.gender_eq"
                            :aria-label="$t('races.show.gender_filters.label')"
                        )
                            b-form-radio(
                                v-for="option in gender_options"
                                :key="String(option.value)"
                                :value="option.value"
                                :title="option.title"
                                :aria-label="option.title"
                            )
                                template(v-if="option.value == null") {{option.text}}
                                template(v-else)
                                    gender-icon(:gender="option.value" aria-hidden="true")
                                    span.sr-only {{option.title}}
                .results-filter-mobile__control(v-if="categoryFilterEnabled")
                    b-form-select(v-model="q.category_eq" :options="race_categories")
                        template(#first)
                            option(value="" selected) -- {{$t('category')}} --
                .results-filter-mobile__control(v-if="extraFilters")
                    b-form-radio-group(size="sm" buttons button-variant="outline-secondary")
                        b-form-radio(
                            v-for="extraFilter in extraFilters" :key="extraFilter.value"
                            v-model="q[extraFilter.term]" :value="extraFilter.value"
                        ) {{extraFilter.text || extraFilter.value }}
                .results-filter-mobile__control(
                    v-for="definition in additionalFilterDefinitions"
                    :key="definition.key"
                )
                    b-form-select(
                        v-model="public_filters[definition.key]"
                        :options="additionalFilterOptions(definition)"
                        :aria-label="definition.label"
                    )
        .row(v-if="features.favorites || (features.split_ranks && 'linear' == race.race_type)")
            .col-sm-12.col-md-6.text-left.mb-2(v-if="features.favorites")
                b-button-group(size="sm")
                    b-btn.btn-sm-block(size="sm" variant="outline-danger" v-show="0 < compare_bibs.length" :pressed.sync="q.bib_in == compare_bibs" @click="favourites")
                        i.fa.fa-star
                        |&nbsp;{{$t('races.show.favourites.protocol')}}

                    compare-button.btn-sm-block( :bibs="compare_bibs" v-show="1 < compare_bibs.length" )
                        i.fa.fa-balance-scale
                        |&nbsp;{{$t('races.show.favourites.compare')}}

                    compare-button.btn-sm-block( :bibs="compare_bibs" :global="true" v-if="!race.is_relay" v-show="1 < compare_bibs.length" ) {{$t('races.show.favourites.compare_global')}}

            .col-sm-12.col-md-auto.ml-md-auto.text-right.mb-2.d-none.d-md-block(v-if="features.split_ranks && 'linear' == race.race_type")
                .results-split-toolbar
                    b-btn.results-split-toolbar__ranks(
                        size="sm" variant="link"
                        v-show="!split_show_mode && showSplitRanksLoadBtn"
                        @click="loadSplitRanks"
                        :pressed="hasSplitRanks"
                        :title="$t('races.show.split_labels.title')"
                    )
                        i.fal.fa-list-ol.mr-1
                        |{{$t('races.show.split_ranks')}}

                    .results-split-toolbar__mode
                        small.results-split-toolbar__label {{$t('races.show.split_labels.label')}}
                        b-form-radio-group.results-split-toolbar__switch(
                            id="btn_splits" size="sm" buttons button-variant="outline-secondary"
                            v-model="split_show_mode"
                            :options="[{text: $t('races.show.split_labels.start'), value: false}, {text: $t('races.show.split_labels.split'), value: true}]"
                        )

        b-table#heats-table(
            small responsive striped head-variant="light"
            :items="heats" :fields="tableFields"
            show-empty
            :empty-text="$t('search.nothing_found')"
            primary-key="bib"
            )

            template(#head(compare)="foo"): i.fa-star(
                :class="q.bib_in == compare_bibs ? 'fa text-success' : 'far'"
                :style="q.bib_in == compare_bibs ? 'cursor: pointer;' : ''"
                @click="q.bib_in == compare_bibs ? click_favs() : ''"
                )

            template(#head(filtered_index))
                span(:title="$t('races.show.filtered_index.title')") {{$t('races.show.filtered_index.label')}}

            template(#cell(filtered_index)="row")
                span.results-filtered-index__value {{filteredSelectionRowNumber(row.index, pageInfo)}}


            template(v-for="field in fields.filter(f => f.colType=='split')" v-slot:[head_slot(field.key)]="data")
                |{{data.label}}
                template(v-if="'transit' != data.field.split.sport")
                    br
                    span.font-xs(:title="$t('races.show.titles.distance_from_start')" v-show="!split_show_mode") {{ data.field.split.distance + ' ' + $t('units.meters')}}
                    span.font-xs(:title="$t('races.show.titles.distance_from_split')" v-show="split_show_mode") {{ data.field.split.split_distance + ' ' + $t('units.meters')}}

            template(v-for="(split, idx) in splits" v-slot:[split_slot(split.number)]="row")
                span(v-show="!split_show_mode") {{ formatted_split_data(row.item, split.name, race.start_by) }}
                sub.px-1(v-if="split_ranks && row.item.bib in split_ranks" v-show="!split_show_mode")
                    |{{split_ranks[row.item.bib][split.name]}}
                    i.fa.fa-arrow-up.text-success(v-if="0 < idx && split_ranks[row.item.bib][split.name] < split_ranks[row.item.bib][splits[idx-1].name]")
                    i.fa.fa-arrow-down.text-danger(v-if="0 < idx && split_ranks[row.item.bib][split.name] > split_ranks[row.item.bib][splits[idx-1].name]")
                span(v-show="split_show_mode") {{ formatted_split_time(row.item, split.name, race.start_by) }}

            template(v-for="field in fields.filter(f => f.colType=='lap_split')" v-slot:[head_slot(field.key)]="data")
                |{{data.label}}
                template(v-if="data.field.lap.distance_display")
                    br
                    span.font-xs(:title="$t('races.show.titles.distance_from_split')") {{ data.field.lap.distance_display + ' ' + $t('units.meters')}}

            template(v-for="field in fields.filter(f => f.colType=='lap_split')" v-slot:[field_slot(field.key)]="row")
                |{{ formatted_lap_split(row.item, field.key) }}

            template(v-for="field in fields.filter(f => f.colType=='rank_compact')" v-slot:[field_slot(field.key)]="row")
              .rank-compact(:aria-label="compactRankAriaLabel(row.item, row.field.rankKeys)")
                span.rank-compact__item(
                  v-for="(rankKey, index) in row.field.rankKeys"
                  :key="rankKey"
                  v-if="row.item[rankKey]"
                  :title="rankTitle(rankKey)"
                )
                  span.rank-compact__separator(v-if="index > 0") |
                  strong {{ row.item[rankKey] }}

            template(#cell(compare)="row")
                toggle-comparison-button(:race_id="race.id" :bib="row.item.bib" :slug="race.slug" :in_compare="in_compare(row.item.bib)" @toggle-compare="toggle_click($event)")
            template(#cell(bib)="row"): span.bib {{row.item.bib}}
            template(#cell(category)="row"): span.category {{row.item.category}}
            template(#cell(country)="row"): country-flag(:country="row.item.country || ''" :title="row.item.country")
            template(#cell(gender)="row"): gender-icon(:gender="row.item.gender")

            template(#cell(name)="row")
              .participant-summary
                a.medal.medal-first(
                  v-if="features.leader_badges && leaderFor(row.item.bib)"
                  href="leaderboard"
                  :title="$t('races.show.titles.rank_abs')"
                  :aria-label="$t('races.show.titles.rank_abs') + ': ' + leaderFor(row.item.bib).rank"
                ) {{ leaderFor(row.item.bib).rank }}
                template(v-if="row.item.status_message")
                  i.fal.fa-exclamation-circle.text-danger.participant-summary__status(:title="row.item.status_message")
                .participant-summary__body
                  a.participant-summary__name(:href="row.item.self")
                    strong {{ row.item.name }}
                  span.participant-details.d-md-none(v-if="mobileInlineFields.length")
                    template(v-for="field in mobileInlineFields")
                      template(v-if="participantFieldValue(row.item, field)")
                        gender-icon.participant-details__icon(
                          v-if="field.key === 'gender'"
                          :key="fieldIdentity(field)"
                          :gender="row.item.gender"
                          :title="field.label"
                        )
                        country-flag.participant-details__flag(
                          v-else-if="field.key === 'country'"
                          :key="fieldIdentity(field)"
                          :country="row.item.country || ''"
                          :title="field.label"
                        )
                        span.badge.badge-light.participant-details__badge(
                          v-else-if="field.mobile === 'badge'"
                          :key="fieldIdentity(field)"
                          :title="field.label"
                        ) {{ participantFieldValue(row.item, field) }}
                        small.text-muted.participant-details__text(v-else :key="fieldIdentity(field)")
                          |{{ participantFieldValue(row.item, field) }}
                  span.participant-details.d-none.d-md-inline(v-if="desktopInlineFields.length")
                    template(v-for="field in desktopInlineFields")
                      template(v-if="participantFieldValue(row.item, field)")
                        gender-icon.participant-details__icon(
                          v-if="field.key === 'gender'"
                          :key="fieldIdentity(field)"
                          :gender="row.item.gender"
                          :title="field.label"
                        )
                        country-flag.participant-details__flag(
                          v-else-if="field.key === 'country'"
                          :key="fieldIdentity(field)"
                          :country="row.item.country || ''"
                          :title="field.label"
                        )
                        span.badge.badge-light.participant-details__badge(
                          v-else-if="field.desktop === 'badge'"
                          :key="fieldIdentity(field)"
                          :title="field.label"
                        ) {{ participantFieldValue(row.item, field) }}
                        small.text-muted.participant-details__text(v-else :key="fieldIdentity(field)")
                          |{{ participantFieldValue(row.item, field) }}
                  small.text-danger.participant-summary__status-message.d-md-none(v-if="row.item.status_message") {{ row.item.status_message }}

            template(#cell(result)="row")
                template(v-if="['Q', 'HC'].includes(row.item.status)") {{row.item.result}}
                template(v-else) {{row.item.status}}
                a(:href="row.item.certificate_url" v-if="race.has_diploma && row.item.status == 'Q' && row.item.certificate_url" rel="nofollow")
                  i.fas.fa-certificate.text-success.ml-1(:title="$t('certificate')")
            template(#cell(points)="row")
                template(v-if="['Q', 'HC'].includes(row.item.status)"):  span.points {{row.item.points}}
                template(v-else) —

            template(#cell(result_distance)="row") {{row.item.result_distance_display || row.item.result_distance}}
            template(#cell(gap)="row") {{row.item.gap_display || protocol_delta(row.item.gap)}}
            template(#cell(diff)="row") {{row.item.diff_display || protocol_delta(row.item.diff)}}
            template(#cell(laps)="row")
                template(v-if="row.item.splits[row.item.splits.length - 1] != undefined") {{row.item.splits[row.item.splits.length - 1].lap}}
                template(v-else) —
            template(#cell(last_lap)="row") {{row.item.last_lap_display || get_last_split_time(row.item)}}

        b-row()
          b-col.my-1(md="6" v-show="1 < pageInfo.totalPages")
            b-pagination.my-0(:total-rows="pageInfo.totalRows" :per-page="pageInfo.perPage" v-model="pageInfo.currentPage")
          .col.text-right.float-right {{$t('races.show.pagination.shown')}} <strong>{{pageInfo.perPage * (pageInfo.currentPage - 1) +1}}-{{pageInfo.perPage * (pageInfo.currentPage - 1) + heats.length}}</strong> {{$t('races.show.pagination.of')}} <strong>{{pageInfo.totalRows}}</strong>


</template>

<script>
import GenderIcon from './gender-icon';
import CompareButton from './compare-button';
import ToggleComparisonButton from './toggle-comparison-button';
import qs from 'qs';
import debounce from '../js/debounce';
import CountryFlag from 'vue-country-flag';
import { formatDurationMs } from '../../lib/datetime';
import { buildRaceResultFields } from '../js/race_results_fields';
import {
  additionalFilterSelectOptions,
  availableAdditionalFilters,
  configuredResultFilters,
  filteredSelectionField,
  filteredSelectionRowNumber,
  initialAdditionalFilterValues,
  initialNameSearch,
  selectedAdditionalFilters
} from '../js/race_results_filters';
import { liveModeFromAction, publicResultsLiveWindowPlan, publicResultsPublicationTerminal } from '../js/public_results_live_state';

const defaultQuery = {
  category_eq: '',
  gender_eq: null,
  name_or_bib_start: null,
  team_ranking_eq: null
};

export default {
  name: 'heats-table',
  props: ['heats', 'race', 'extraFilters'],
  data() {
    const resultsConfig = this.race.results_config || {};
    const filterDefinitions = configuredResultFilters(resultsConfig);
    const resultFilters = window.general.result_filters || {};
    const selectedFilters = resultFilters.selected || {};
    const publicFilters = initialAdditionalFilterValues(filterDefinitions, selectedFilters);
    const nameSearch = initialNameSearch(window.general.q);
    const configuredColumns = (resultsConfig.columns || []).map((column) => ({ ...column }));
    const pointsColumn = configuredColumns.find((column) => column.key === 'points');
    const countryColumn = configuredColumns.find((column) => column.key === 'country');
    if (window.general.hide_points && pointsColumn) pointsColumn.enabled = false;
    if (window.general.show_country && countryColumn) countryColumn.enabled = true;

    return {
      split_show_mode: JSON.parse(localStorage.getItem('split_show_mode') || false),
      leaders: window.general.leaders || [],
      split_ranks: window.general.split_ranks,
      features: resultsConfig.features || {},
      rank_layout: resultsConfig.rank_layout || {},
      participant_fields: resultsConfig.participant_fields || [],
      custom_fields: resultsConfig.custom_fields || [],
      filter_definitions: filterDefinitions,
      result_filter_options: resultFilters.options || {},
      public_filters: publicFilters,
      additionalFiltersOpen: false,
      live_updates: resultsConfig.live_updates || {},
      liveActive: Boolean(resultsConfig.live_updates?.realtime),
      liveSubscribed: false,
      liveSubscriptionAttempted: false,
      liveResponseEpoch: 0,
      liveTerminated: publicResultsPublicationTerminal(this.race),
      liveAwaitingStart: false,
      liveStartTimer: null,
      liveRetryTimer: null,
      liveEndTimer: null,
      fields: buildRaceResultFields({
        race: this.race,
        columns: configuredColumns,
        rankLayout: resultsConfig.rank_layout,
        participantFields: resultsConfig.participant_fields,
        customFields: resultsConfig.custom_fields,
        features: resultsConfig.features,
        translate: (key) => window.i18n.t(key)
      }),
      pageInfo: window.general.page_info,
      compare_bibs: [],
      gender_options: [
        {
          text: window.i18n.t('races.show.gender_filters.all'),
          title: window.i18n.t('races.show.gender_filters.all_title'),
          value: null
        },
        {
          title: window.i18n.t('races.show.gender_filters.male_title'),
          value: 1
        },
        {
          title: window.i18n.t('races.show.gender_filters.female_title'),
          value: 2
        }
      ],
      race_categories: window.general.race_categories,
      q: window.general.q,
      showSplitRanksLoadBtn: true,
      bib_name_q: nameSearch.value,
      bib_name_exact: nameSearch.exact,
      bib_name_term: { false: 'name_or_bib_start', true: 'name_or_bib_eq' }
    };
  },
  channels: {
    ProtocolHeatsChannel: {
      connected() {
        this.liveSubscribed = true;
        this.liveAwaitingStart = false;
        this.clearLiveTimer('liveRetryTimer');
      },
      rejected() {
        this.liveSubscribed = false;
        if (this.liveAwaitingStart) this.scheduleLiveRetry();
      },
      received(data) {
        if (this.liveTerminated) return;
        if (data.action === 'publication' && publicResultsPublicationTerminal(data.publication)) {
          this.liveResponseEpoch += 1;
          this.liveTerminated = true;
          this.stopLiveUpdates();
          window.location.reload();
          return;
        }
        if (data.heat) {
          const heats = this.heats.map((heat) => (
            heat.bib === data.heat.bib ? { ...data.heat, _rowVariant: 'highlight' } : heat
          ));
          this.$emit('input', heats);
        }
        if (data.heats) this.$emit('input', data.heats);
        if (data.action === 'update') this.search();
        this.setLiveMode(liveModeFromAction(this.liveActive, data.action));
      }
    }
  },
  computed: {
    hasSplitRanks() {
      return this.split_ranks != null && Object.keys(this.split_ranks).length > 0;
    },
    debouncedSearch() {
      return debounce(() => this.search(), 500);
    },
    splits() {
      return this.race.splits.filter((split) => split.is_split);
    },
    lap_splits() {
      return this.race.lap_splits || [];
    },
    unwrap_laps() {
      return this.race.unwrap_laps && this.lap_splits.length > 0;
    },
    split_names() {
      return this.splits.map((split) => split.name);
    },
    q_is_default() {
      return JSON.stringify(this.q) === JSON.stringify(defaultQuery);
    },
    public_filters_are_default() {
      return Object.values(this.public_filters).every((value) => value == null || value === '');
    },
    hasActiveFilters() {
      return !this.q_is_default || !this.public_filters_are_default;
    },
    categoryFilterEnabled() {
      return this.filterEnabled('category');
    },
    genderFilterEnabled() {
      return this.filterEnabled('gender');
    },
    additionalFilterDefinitions() {
      return availableAdditionalFilters(this.filter_definitions, this.result_filter_options);
    },
    additionalFilterCount() {
      return Object.values(this.public_filters).filter((value) => value != null && value !== '').length;
    },
    hasIndexedSelection() {
      return this.additionalFilterCount > 0;
    },
    hasFilterPanelControls() {
      return this.categoryFilterEnabled
        || this.genderFilterEnabled
        || (this.extraFilters || []).length > 0
        || this.additionalFilterDefinitions.length > 0;
    },
    filterPanelCount() {
      const categoryCount = this.categoryFilterEnabled && this.q.category_eq ? 1 : 0;
      const genderCount = this.genderFilterEnabled && this.q.gender_eq != null ? 1 : 0;
      const extraFilterCount = (this.extraFilters || []).filter((filter) => {
        const value = this.q[filter.term];
        return value != null && value !== '';
      }).length;

      return categoryCount + genderCount + extraFilterCount + this.additionalFilterCount;
    },
    additionalFiltersId() {
      return `race-result-filters-${this.race.id}`;
    },
    tableFields() {
      if (!this.hasIndexedSelection) return this.fields;

      const fields = this.fields.slice();
      const index = fields[0]?.key === 'compare' ? 1 : 0;
      fields.splice(index, 0, filteredSelectionField(this.$t('races.show.filtered_index.label')));
      return fields;
    },
    displayFields() {
      const participantFields = this.participant_fields.map((field) => ({ ...field, sourceType: 'participant' }));
      const customFields = this.custom_fields.map((field) => ({ ...field, sourceType: 'custom' }));
      return participantFields.concat(customFields);
    },
    mobileInlineFields() {
      return this.displayFields.filter((field) => ['badge', 'text'].includes(field.mobile));
    },
    desktopInlineFields() {
      return this.displayFields.filter((field) => ['badge', 'text'].includes(field.desktop));
    }
  },
  mounted() {
    this.prepareLiveUpdates();
    if (this.race.is_relay) {
      this.gender_options.push({
        title: this.$t('races.show.gender_filters.mixed_title'),
        value: 3
      });
    }
    if (this.features.favorites) {
      this.compare_bibs = JSON.parse(localStorage.getItem(`comparison_${this.race.id}`)) || [];
    }
  },
  beforeDestroy() {
    this.stopLiveUpdates();
  },
  methods: {
    head_slot(key) {
      return `head(${key})`;
    },
    split_slot(splitNumber) {
      return `cell(split_${splitNumber})`;
    },
    field_slot(key) {
      return `cell(${key})`;
    },
    leaderFor(bib) {
      return this.leaders.find((leader) => String(leader.bib) === String(bib));
    },
    participantFieldValue(heat, field) {
      const value = field.sourceType === 'custom' ? heat.public_fields?.[field.key] : heat[field.key];
      if (Array.isArray(value)) return value.join(' / ');
      if (value == null || value === '') return null;
      return String(value);
    },
    fieldIdentity(field) {
      return `${field.sourceType}_${field.key}`;
    },
    filterEnabled(key) {
      return this.filter_definitions.some((definition) => definition.key === key);
    },
    additionalFilterOptions(definition) {
      return additionalFilterSelectOptions(
        definition,
        this.result_filter_options[definition.key] || [],
        this.$i18n.locale
      );
    },
    filteredSelectionRowNumber,
    rankTitle(key) {
      return {
        rank_abs: this.$t('ranks.abs'),
        rank_gender: this.$t('ranks.gender'),
        rank_category: this.$t('ranks.category')
      }[key];
    },
    compactRankAriaLabel(heat, rankKeys) {
      return rankKeys
        .filter((key) => heat[key])
        .map((key) => `${this.rankTitle(key)}: ${heat[key]}`)
        .join(', ');
    },
    prepareLiveUpdates() {
      if (!this.race || this.liveTerminated) return;

      const plan = publicResultsLiveWindowPlan(this.live_updates);
      if (!plan.enabled || plan.endsAt <= Date.now()) return;

      this.scheduleLiveEnd(plan.endsAt);
      this.liveAwaitingStart = true;
      if (plan.shouldSubscribe) {
        this.subscribeLiveUpdates();
      } else if (plan.shouldScheduleStart) {
        this.scheduleLiveStart(plan.startsAt);
      }
    },
    scheduleLiveStart(startsAt) {
      const delay = startsAt - Date.now();
      if (delay <= 0) {
        this.liveAwaitingStart = true;
        this.subscribeLiveUpdates();
      } else {
        this.liveStartTimer = setTimeout(
          () => this.scheduleLiveStart(startsAt),
          Math.min(delay, 6 * 60 * 60 * 1000)
        );
      }
    },
    scheduleLiveEnd(endsAt) {
      const delay = endsAt - Date.now();
      if (delay <= 0) {
        this.stopLiveUpdates();
      } else {
        this.liveEndTimer = setTimeout(
          () => this.scheduleLiveEnd(endsAt),
          Math.min(delay, 6 * 60 * 60 * 1000)
        );
      }
    },
    subscribeLiveUpdates() {
      if (this.liveSubscribed || this.liveTerminated) return;

      this.liveSubscriptionAttempted = true;
      this.$cable.subscribe({ channel: 'ProtocolHeatsChannel', race_id: this.race.id });
    },
    scheduleLiveRetry() {
      if (this.liveRetryTimer != null) return;

      this.liveRetryTimer = setTimeout(() => {
        this.liveRetryTimer = null;
        if (this.liveAwaitingStart) this.subscribeLiveUpdates();
      }, 30 * 1000);
    },
    clearLiveTimer(name) {
      if (this[name] == null) return;

      clearTimeout(this[name]);
      this[name] = null;
    },
    stopLiveUpdates() {
      ['liveStartTimer', 'liveRetryTimer', 'liveEndTimer'].forEach((name) => this.clearLiveTimer(name));
      if (this.liveSubscriptionAttempted) this.$cable.unsubscribe('ProtocolHeatsChannel');
      this.liveSubscriptionAttempted = false;
      this.liveSubscribed = false;
      this.liveAwaitingStart = false;
      this.setLiveMode(false);
    },
    setLiveMode(value) {
      const live = Boolean(value);
      if (this.liveActive === live) return;

      this.liveActive = live;
      this.$emit('live-mode', live);
    },
    query(params) {
      return qs.stringify(params, { arrayFormat: 'brackets', encode: true });
    },
    queueFilterSearch() {
      if (this.pageInfo.currentPage === 1) {
        this.debouncedSearch();
      } else {
        this.pageInfo.currentPage = 1;
      }
    },
    search() {
      const params = {};
      if (!this.q_is_default) params.q = this.q;

      const publicFilters = selectedAdditionalFilters(this.public_filters);
      if (Object.keys(publicFilters).length) params.filters = publicFilters;
      if (this.pageInfo.currentPage > 1 || Object.keys(params).length) params.page = this.pageInfo.currentPage;
      if (this.split_ranks) params.split_ranks = true;

      const axiosConfig = {
        params,
        paramsSerializer: (queryParams) => this.query(queryParams)
      };
      const publicationEpoch = this.liveResponseEpoch;
      this.axios.get(`${this.race.self}.json`, axiosConfig)
        .then((response) => {
          if (publicationEpoch !== this.liveResponseEpoch) return;

          this.$emit('input', response.data.items);
          this.split_ranks = response.data.split_ranks;
          this.pageInfo = response.data.page_info;
          const query = this.query(params);
          if (query) history.pushState({}, null, `?${query}`);
        })
        .catch((error) => console.error(error));
    },
    favourites() {
      if (this.q.bib_in === this.compare_bibs) {
        this.q = { ...defaultQuery };
        history.pushState({}, null, document.location.pathname);
      } else {
        this.q = { bib_in: this.compare_bibs, page: this.pageInfo.currentPage };
      }
    },
    click_favs() {
      this.compare_bibs = [];
      this.reset_filter();
    },
    reset_filter() {
      this.q = { ...defaultQuery };
      Object.keys(this.public_filters).forEach((key) => this.$set(this.public_filters, key, ''));
      this.bib_name_exact = false;
      this.bib_name_q = null;
      history.pushState({}, null, document.location.pathname);
    },
    toggle_click(bib) {
      const index = this.compare_bibs.indexOf(bib);
      if (index === -1) {
        this.compare_bibs.push(bib);
      } else {
        this.compare_bibs.splice(index, 1);
      }
    },
    in_compare(bib) {
      return this.compare_bibs.indexOf(bib) !== -1;
    },
    get_last_split_time(heat) {
      const { length } = heat.splits;
      if (length <= 1) return '—';

      const seconds = heat.splits[length - 1].absolute - heat.splits[length - 2].absolute;
      return this.$options.filters.format_time(seconds);
    },
    protocol_delta(value) {
      return this.race.finish_by === 'duration'
        ? this.format_distance(value)
        : this.$options.filters.format_time(value);
    },
    format_distance(value) {
      if (value == null || Number.isNaN(Number(value))) return '—';
      return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} м`;
    },
    split_data(heat, splitName, startBy) {
      const heatSplit = heat.splits.find((item) => item.name === splitName);
      return heatSplit ? heatSplit[startBy] : '—';
    },
    split_time(heat, splitName, startBy) {
      const index = this.split_names.indexOf(splitName);
      return index > 0
        ? this.split_data(heat, splitName, startBy) - this.split_data(heat, this.split_names[index - 1], startBy)
        : this.split_data(heat, splitName, startBy);
    },
    formatted_split_data(heat, splitName, startBy) {
      const display = heat.splits_display?.[splitName]?.[startBy];
      return display || this.$options.filters.format_time(this.split_data(heat, splitName, startBy));
    },
    formatted_split_time(heat, splitName, startBy) {
      const display = heat.split_durations_display?.[splitName];
      return display || this.$options.filters.format_time(this.split_time(heat, splitName, startBy));
    },
    formatted_lap_split(heat, key) {
      return heat.lap_splits_display?.[key] || '—';
    },
    loadSplitRanks() {
      const axiosConfig = { params: { bibs: this.heats.map((heat) => heat.bib) } };
      this.axios.get(this.race.self.replace('results', 'split_ranks'), axiosConfig)
        .then((response) => {
          this.split_ranks = response.data;
          this.showSplitRanksLoadBtn = false;
        })
        .catch(() => {
          this.split_ranks = [];
        });
    }
  },
  filters: {
    format_time(absolute) {
      if (absolute == null || Number.isNaN(Number(absolute))) return '—';
      return formatDurationMs(absolute * 1000) || '—';
    }
  },
  watch: {
    q: {
      handler() {
        this.queueFilterSearch();
      },
      deep: true
    },
    public_filters: {
      handler() {
        this.queueFilterSearch();
      },
      deep: true
    },
    'pageInfo.currentPage'() {
      this.search();
    },
    bib_name_q() {
      this.$delete(this.q, this.bib_name_term[!this.bib_name_exact]);
      this.$set(this.q, this.bib_name_term[this.bib_name_exact], this.bib_name_q);
    },
    bib_name_exact() {
      this.$delete(this.q, this.bib_name_term[!this.bib_name_exact]);
      this.$set(this.q, this.bib_name_term[this.bib_name_exact], this.bib_name_q);
    },
    compare_bibs() {
      localStorage.setItem(`comparison_${this.race.id}`, JSON.stringify(this.compare_bibs));
    },
    split_show_mode() {
      localStorage.setItem('split_show_mode', this.split_show_mode);
    }
  },
  components: {
    'gender-icon': GenderIcon,
    'compare-button': CompareButton,
    'toggle-comparison-button': ToggleComparisonButton,
    'country-flag': CountryFlag
  }
};
</script>


<style>
.results-filter-toolbar {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.results-filter-toolbar__search {
  flex: 1 1 18rem;
  min-width: 14rem;
}

.results-filter-toolbar__select {
  flex: 1 1 12rem;
  max-width: 18rem;
  min-width: 10rem;
}

.results-filter-toolbar__gender,
.results-filter-toolbar__choices,
.results-filter-toolbar__reset {
  flex: 0 0 auto;
}

.results-filter-toolbar__search .custom-control,
.results-gender-filter .custom-control,
.results-filter-toolbar__choices .custom-control {
  margin-bottom: 0;
}

.results-filter-toolbar__reset {
  margin-left: auto;
}

.results-filter-mobile__control {
  margin-bottom: 0.5rem;
}

.results-filter-mobile__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.results-filter-toggle.btn {
  align-items: center;
  background: #f4f5f7;
  border: 1px solid #e1e5e9;
  border-radius: 0.5rem;
  box-shadow: none;
  color: #495057;
  display: inline-flex;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.42rem 0.65rem;
}

.results-filter-toggle.btn:hover,
.results-filter-toggle.btn:focus,
.results-filter-toggle.btn.is-open {
  background: #e9ecef;
  border-color: #d7dce1;
  color: #212529;
}

.results-filter-toggle.btn:focus {
  box-shadow: 0 0 0 0.15rem rgba(108, 117, 125, 0.16);
}

.results-filter-toggle__count {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.68rem;
  justify-content: center;
  line-height: 1;
  min-width: 1.15rem;
  padding: 0.2rem 0.34rem;
}

.results-filter-reset.btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0.4rem;
  box-shadow: none;
  color: #6c757d;
  font-size: 0.82rem;
  font-weight: 500;
  padding: 0.38rem 0.5rem;
  text-decoration: none;
}

.results-filter-reset.btn:hover,
.results-filter-reset.btn:focus {
  background: #f4f5f7;
  border-color: #e1e5e9;
  box-shadow: none;
  color: #343a40;
  text-decoration: none;
}

#heats-table .results-filtered-index {
  color: #6c757d;
  font-variant-numeric: tabular-nums;
  font-weight: 400;
  white-space: nowrap;
  width: 1%;
}

.results-split-toolbar,
.results-gender-filter {
  align-items: center;
  background: #f4f5f7;
  border: 1px solid #e1e5e9;
  border-radius: 0.55rem;
  box-shadow: 0 1px 2px rgba(26, 32, 44, 0.06);
  display: inline-flex;
  gap: 0.35rem;
  padding: 0.2rem;
}

.results-split-toolbar__ranks.btn {
  border: 0;
  border-radius: 0.35rem;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.35rem 0.55rem;
  text-decoration: none;
}

.results-split-toolbar__ranks.btn:hover,
.results-split-toolbar__ranks.btn:focus {
  background: #fff;
  box-shadow: none;
  color: #212529;
  text-decoration: none;
}

.results-split-toolbar__mode {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.results-split-toolbar__label,
.results-gender-filter__label {
  color: #6c757d;
  font-size: 0.78rem;
  margin: 0 0.15rem 0 0.25rem;
  white-space: nowrap;
}

.results-split-toolbar__switch,
.results-gender-filter__switch {
  background: #e4e7eb;
  border-radius: 0.4rem;
  padding: 0.12rem;
}

.results-split-toolbar__switch .btn,
.results-gender-filter__switch .btn {
  background: transparent !important;
  border-color: transparent !important;
  border-radius: 0.3rem !important;
  box-shadow: none !important;
  color: #495057 !important;
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1.25;
  padding: 0.3rem 0.65rem;
}

.results-split-toolbar__switch .btn.active,
.results-gender-filter__switch .btn.active {
  background: #fff !important;
  border-color: #d7dce1 !important;
  box-shadow: 0 1px 3px rgba(26, 32, 44, 0.14) !important;
  color: #212529 !important;
}

html[data-theme="dark"] .results-split-toolbar,
html[data-theme="dark"] .results-gender-filter {
  background: #20252a;
  border-color: #343a40;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

html[data-theme="dark"] .results-split-toolbar__ranks.btn,
html[data-theme="dark"] .results-split-toolbar__label,
html[data-theme="dark"] .results-gender-filter__label {
  color: #adb5bd;
}

html[data-theme="dark"] .results-split-toolbar__ranks.btn:hover,
html[data-theme="dark"] .results-split-toolbar__ranks.btn:focus {
  background: #2b3137;
  color: #f8f9fa;
}

html[data-theme="dark"] .results-split-toolbar__switch,
html[data-theme="dark"] .results-gender-filter__switch {
  background: #15191d;
}

html[data-theme="dark"] .results-split-toolbar__switch .btn,
html[data-theme="dark"] .results-gender-filter__switch .btn {
  color: #adb5bd !important;
}

html[data-theme="dark"] .results-split-toolbar__switch .btn.active,
html[data-theme="dark"] .results-gender-filter__switch .btn.active {
  background: #343a40 !important;
  border-color: #495057 !important;
  color: #fff !important;
}

html[data-theme="dark"] .results-filter-toggle.btn {
  background: #20252a;
  border-color: #343a40;
  color: #ced4da;
}

html[data-theme="dark"] .results-filter-toggle.btn:hover,
html[data-theme="dark"] .results-filter-toggle.btn:focus,
html[data-theme="dark"] .results-filter-toggle.btn.is-open {
  background: #2b3137;
  border-color: #495057;
  color: #fff;
}

html[data-theme="dark"] .results-filter-toggle__count {
  background: #adb5bd;
  color: #212529;
}

html[data-theme="dark"] .results-filter-reset.btn {
  color: #adb5bd;
}

html[data-theme="dark"] .results-filter-reset.btn:hover,
html[data-theme="dark"] .results-filter-reset.btn:focus {
  background: #20252a;
  border-color: #343a40;
  color: #fff;
}

table td {
  white-space: normal;
}

#heats-table th,
#heats-table td {
  vertical-align: middle;
}

#heats-table .participant-summary {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

#heats-table .participant-summary__name {
  display: inline-block;
  min-width: 0;
}

#heats-table .participant-summary__body {
  min-width: 0;
}

#heats-table .participant-summary__status {
  flex: 0 0 auto;
}

#heats-table .medal {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  min-width: 1.75rem;
  min-height: 1.75rem;
  padding: 0.2rem 0.45rem;
  line-height: 1;
  font-size: 1.15rem;
  font-weight: 700;
}

#heats-table .participant-details {
  margin-left: 0.35rem;
  line-height: inherit;
}

#heats-table .participant-details__icon,
#heats-table .participant-details__flag,
#heats-table .participant-details__badge,
#heats-table .participant-details__text {
  margin-right: 0.35rem;
}

#heats-table .participant-details__badge {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

#heats-table .participant-details__flag {
  font-size: 0.75rem;
}

#heats-table .participant-summary__status-message {
  display: block;
  margin-top: 0.15rem;
}

#heats-table .rank-compact {
  display: inline-flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.3rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

#heats-table .rank-compact__item {
  display: inline-flex;
  align-items: baseline;
}

#heats-table .rank-compact__separator {
  color: #6c757d;
  margin-right: 0.3rem;
  font-weight: 400;
}

@media (max-width: 767.98px) {
  #heats-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  #heats-table th,
  #heats-table td {
    padding: 0.45rem 0.3rem;
    vertical-align: middle;
  }

  #heats-table .participant-summary__name {
    display: inline-block;
    min-height: 2rem;
    padding: 0.25rem 0;
  }
}
</style>
